import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import api from '../lib/api';
import { goPro } from '../lib/checkout';
import { Headphones, BookOpen, PenTool, Mic, Clock, FileText, ArrowLeft, Play, Lock, CheckCircle, ShieldAlert, RotateCcw } from 'lucide-react';

const SKILL_META = {
  listening: { icon: <Headphones size={28} />, color: '#3B82F6', bg: '#EFF6FF', label: 'Listening', shortDesc: '6 Parts, ~47-55 min' },
  reading: { icon: <BookOpen size={28} />, color: '#F59E0B', bg: '#FFFBEB', label: 'Reading', shortDesc: '4 Parts, ~43-56 min' },
  writing: { icon: <PenTool size={28} />, color: '#EF4444', bg: '#FEF2F2', label: 'Writing', shortDesc: '2 Tasks, ~53 min' },
  speaking: { icon: <Mic size={28} />, color: '#10B981', bg: '#ECFDF5', label: 'Speaking', shortDesc: '8 Tasks, ~15-20 min' },
};

export default function ExamDetail() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastAttempt, setLastAttempt] = useState(null);
  const [resumeDialog, setResumeDialog] = useState(null); // { target: 'full' | skill } | null
  const [abandoning, setAbandoning] = useState(false);
  
  const isPro = user?.subscription_status && user.subscription_status !== 'free';
  const { entryLabel } = useTrial();

  useEffect(() => {
    api.get(`/api/exams/${examId}`).then(r => {
      setExam(r.data);
      if (r.data?.last_attempt) setLastAttempt(r.data.last_attempt);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [examId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!exam) {
    return (
      <DashboardLayout>
        <p className="text-neutral-500">Exam not found</p>
      </DashboardLayout>
    );
  }

  // Group sections by skill
  const skillSections = {};
  (exam.sections || []).forEach((s, idx) => {
    const sk = s.skill_id;
    if (!skillSections[sk]) skillSections[sk] = { sections: [], totalQuestions: 0, totalTime: 0 };
    skillSections[sk].sections.push({ ...s, globalIndex: idx });
    skillSections[sk].totalQuestions += (s.question_ids || []).length;
    skillSections[sk].totalTime += (s.time_limit_seconds || 0);
  });

  const skillOrder = ['listening', 'reading', 'writing', 'speaking'];

  const startSection = (skill) => {
    if (lastAttempt?.status === 'in_progress') {
      setResumeDialog({ target: skill });
      return;
    }
    // Find the first section index for this skill
    const firstSection = skillSections[skill]?.sections?.[0];
    const sectionIndex = firstSection?.globalIndex || 0;
    navigate(`/exams/${examId}/take?section=${sectionIndex}&skill=${skill}`);
  };

  const startFullExam = () => {
    if (lastAttempt?.status === 'in_progress') {
      setResumeDialog({ target: 'full' });
      return;
    }
    navigate(`/exams/${examId}/take`);
  };

  const doResume = () => {
    const tgt = resumeDialog?.target;
    setResumeDialog(null);
    if (tgt === 'full' || !tgt) {
      navigate(`/exams/${examId}/take`);
    } else {
      const firstSection = skillSections[tgt]?.sections?.[0];
      const sectionIndex = firstSection?.globalIndex || 0;
      navigate(`/exams/${examId}/take?section=${sectionIndex}&skill=${tgt}`);
    }
  };

  const doStartOver = async () => {
    const tgt = resumeDialog?.target;
    setAbandoning(true);
    try {
      await api.post(`/api/exams/${examId}/abandon`);
      setLastAttempt(prev => prev ? { ...prev, status: 'abandoned' } : prev);
      setResumeDialog(null);
      if (tgt === 'full' || !tgt) {
        navigate(`/exams/${examId}/take`);
      } else {
        const firstSection = skillSections[tgt]?.sections?.[0];
        const sectionIndex = firstSection?.globalIndex || 0;
        navigate(`/exams/${examId}/take?section=${sectionIndex}&skill=${tgt}`);
      }
    } catch (e) {
      console.error('Failed to abandon attempt', e);
    } finally {
      setAbandoning(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl" data-testid="exam-detail-page">
        <Link to="/exams" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 mb-4 sm:mb-6">
          <ArrowLeft size={14} />Back to Exams
        </Link>

        {/* Exam Header */}
        <div className="bg-white rounded-xl sm:rounded-2xl border border-neutral-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold text-neutral-900" data-testid="exam-detail-title">{exam.name}</h1>
                {exam.is_free && <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">{entryLabel}</span>}
                {!exam.is_free && <span className="px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-600 rounded-full flex items-center gap-1"><Lock size={10} />Pro</span>}
              </div>
              <p className="text-neutral-600 text-sm mb-3 sm:mb-4">{exam.description}</p>
              <div className="flex items-center gap-4 text-xs sm:text-sm text-neutral-500">
                <span className="flex items-center gap-1"><FileText size={14} />{exam.total_questions} questions</span>
                <span className="flex items-center gap-1"><Clock size={14} />{exam.estimated_duration_minutes || 180} minutes</span>
              </div>
            </div>
          </div>

          {/* Last attempt info */}
          {lastAttempt?.status === 'completed' && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-green-700 text-sm">
                <CheckCircle size={16} className="shrink-0" />
                Last attempt: CLB {lastAttempt.overall_score} — {new Date(lastAttempt.completed_at || lastAttempt.created_at).toLocaleDateString()}
              </div>
              <Link to={`/exams/${examId}/results/${lastAttempt.id}`} className="text-sm text-primary hover:underline font-medium" data-testid="view-last-results">
                View Results
              </Link>
            </div>
          )}
          {lastAttempt?.status === 'in_progress' && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid="in-progress-banner">
              <div className="flex items-center gap-2 text-blue-700 text-sm">
                <Clock size={16} className="shrink-0" />
                You have an attempt in progress — started {new Date(lastAttempt.started_at || lastAttempt.created_at).toLocaleDateString()}
              </div>
              <button onClick={() => setResumeDialog({ target: 'full' })} className="text-left sm:text-right text-sm text-primary hover:underline font-medium" data-testid="banner-resume-btn">
                Continue or Start Over
              </button>
            </div>
          )}

          {/* Full Exam CTA */}
          <div className="mt-5 sm:mt-6">
            {!exam.is_free && !exam.unlocked && !isPro ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="exam-paywall">
                <div className="flex items-center gap-3 mb-3">
                  <ShieldAlert size={24} className="text-amber-600 shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-900">Pro Plan Required</p>
                    <p className="text-sm text-amber-700">This mock exam is available for Pro subscribers only.</p>
                  </div>
                </div>
                <button onClick={() => goPro()} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition" data-testid="upgrade-to-take-exam">
                  <Lock size={16} />Upgrade to Pro to Unlock
                </button>
              </div>
            ) : (
              <button onClick={startFullExam}
                className="w-full sm:w-auto px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-primary-700 transition flex items-center justify-center gap-2"
                data-testid="start-full-exam-btn">
                <Play size={18} />Take Full Exam (All 4 Skills)
              </button>
            )}
          </div>
        </div>

        {/* Section Picker */}
        <div className="mb-3 sm:mb-4">
          <h2 className="text-base md:text-lg font-semibold text-neutral-900 mb-1">Or practice by section</h2>
          <p className="text-sm text-neutral-500">Choose a specific skill to practice. You can take each section independently.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
          {skillOrder.map(skill => {
            const meta = SKILL_META[skill];
            const data = skillSections[skill];
            if (!data) return null;
            const isLocked = !exam.is_free && !exam.unlocked && !isPro;

            return (
              <button key={skill} onClick={() => isLocked ? goPro(navigate) : startSection(skill)}
                className={`text-left bg-white rounded-xl border p-4 sm:p-5 transition group ${isLocked ? 'border-neutral-200 opacity-70' : 'border-neutral-200 hover:border-primary/30 hover:shadow-md active:bg-neutral-50'}`}
                data-testid={`section-card-${skill}`}>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: meta.bg, color: meta.color }}>
                    {meta.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-neutral-900 text-base sm:text-lg group-hover:text-primary transition">{meta.label}</h3>
                    <p className="text-xs sm:text-sm text-neutral-500 mt-0.5">{meta.shortDesc}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-neutral-400">
                      <span>{data.sections.length} part{data.sections.length > 1 ? 's' : ''}</span>
                      <span>{data.totalQuestions} questions</span>
                      <span>{Math.round(data.totalTime / 60)} min</span>
                    </div>
                  </div>
                  <div className="text-neutral-300 group-hover:text-primary transition mt-2">
                    <Play size={20} />
                  </div>
                </div>

                {/* Part list */}
                <div className="mt-3 pt-3 border-t border-neutral-100 space-y-1">
                  {data.sections.map((sec, i) => (
                    <div key={i} className="text-xs text-neutral-500 flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-neutral-100 flex items-center justify-center text-neutral-600 font-medium">{i + 1}</span>
                      <span>{sec.name}</span>
                      <span className="ml-auto">{(sec.question_ids || []).length}q</span>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Score Targets */}
        <div className="mt-4 sm:mt-6 bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-5">
          <h3 className="font-medium text-neutral-900 mb-3">About This Exam</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">38</div>
              <div className="text-neutral-500">Listening Qs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-600">38</div>
              <div className="text-neutral-500">Reading Qs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-rose-600">2</div>
              <div className="text-neutral-500">Writing Tasks</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">8</div>
              <div className="text-neutral-500">Speaking Tasks</div>
            </div>
          </div>
        </div>
      </div>

      {/* Continue or Start Over modal */}
      {resumeDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="resume-dialog">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <Clock size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 text-lg">You have an attempt in progress</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Would you like to continue where you left off, or start fresh? Starting over will discard your current progress.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <button
                onClick={doResume}
                disabled={abandoning}
                className="flex-1 px-4 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary-700 transition flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="resume-continue-btn">
                <Play size={16} />Continue
              </button>
              <button
                onClick={doStartOver}
                disabled={abandoning}
                className="flex-1 px-4 py-2.5 bg-white border border-neutral-300 text-neutral-700 font-semibold rounded-lg hover:bg-neutral-50 transition flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="resume-startover-btn">
                <RotateCcw size={16} />{abandoning ? 'Starting Over...' : 'Start Over'}
              </button>
              <button
                onClick={() => setResumeDialog(null)}
                disabled={abandoning}
                className="px-3 py-2.5 text-sm text-neutral-500 hover:text-neutral-700 transition disabled:opacity-60"
                data-testid="resume-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
